import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * ChapterRail — a fixed right-edge progressive-disclosure tour rail.
 *
 * The five chapters map to the home page's narrative anchors. Each Roman
 * numeral is dim by default; it brightens to rose-gold once its corresponding
 * section has been at least 50% in view. Subsequent (locked) chapters remain
 * muted until the previous one has been "unlocked" — the rail itself rewards
 * forward motion, making the navigation feel like a guided tour rather than
 * a menu.
 *
 * Desktop only. On smaller widths the mobile drawer (Nav.jsx) shows the same
 * five labels and respects the same disclosure logic.
 */

export const CHAPTERS = [
  { id: "hero",      numeral: "I",   label: "The Light" },
  { id: "heritage",  numeral: "II",  label: "The House" },
  { id: "craft",     numeral: "III", label: "The Atelier" },
  { id: "tasting",   numeral: "IV",  label: "The Liquid" },
  { id: "gallery",   numeral: "V",   label: "The Object" },
];

const RAIL_THRESHOLD = 0.32;

function useChapterProgress() {
  // `unlockedUpTo` is the index of the furthest chapter that has crossed the
  // viewport threshold. Active = the latest chapter currently centred.
  const [unlockedUpTo, setUnlockedUpTo] = useState(0);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const sections = CHAPTERS.map((c) => document.getElementById(c.id)).filter(Boolean);
    if (sections.length === 0) return undefined;

    const onScroll = () => {
      const viewportY = window.innerHeight * RAIL_THRESHOLD;
      let furthest = 0;
      let latest = 0;
      sections.forEach((el, i) => {
        const rect = el.getBoundingClientRect();
        if (rect.top <= viewportY) furthest = Math.max(furthest, i);
        if (rect.top <= window.innerHeight * 0.5) latest = i;
      });
      setUnlockedUpTo(furthest);
      setActive(latest);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return { unlockedUpTo, active };
}

function chapterOpacity({ isActive, unlocked }) {
  if (isActive) return 1;
  if (unlocked) return 0.55;
  return 0.22;
}

function ChapterLabel({ label }) {
  return (
    <span
      className="font-mono text-[10px] tracking-[0.32em] uppercase opacity-0 group-hover:opacity-100 transition-opacity duration-400"
      style={{ color: "var(--noor-vellum)" }}
    >
      {label}
    </span>
  );
}

function ChapterNumeral({ numeral, isActive }) {
  return (
    <span
      className="font-display italic"
      style={{
        fontSize: isActive ? "1.05rem" : "0.82rem",
        color: isActive ? "var(--noor-rosegold)" : "var(--noor-vellum)",
        letterSpacing: "0.02em",
        transition: "all .45s cubic-bezier(.22,1,.36,1)",
      }}
    >
      {numeral}
    </span>
  );
}

function ChapterRule({ isActive }) {
  return (
    <span
      className="block h-px transition-all duration-500"
      style={{
        width: isActive ? 28 : 14,
        background: isActive ? "var(--noor-rosegold)" : "var(--border-subtle)",
      }}
    />
  );
}

function ChapterButton({ chapter, isActive, unlocked, onJump }) {
  const isLocked = !unlocked;
  return (
    <button
      data-testid={`chapter-${chapter.id}`}
      onClick={() => onJump(chapter.id, isLocked)}
      disabled={isLocked}
      className="group flex items-center gap-3 transition-all duration-500"
      style={{
        cursor: isLocked ? "default" : "pointer",
        opacity: chapterOpacity({ isActive, unlocked }),
      }}
    >
      <ChapterLabel label={chapter.label} />
      <ChapterNumeral numeral={chapter.numeral} isActive={isActive} />
      <ChapterRule isActive={isActive} />
    </button>
  );
}

function useChapterJump() {
  const location = useLocation();
  const navigate = useNavigate();
  return (id, isLocked) => {
    if (isLocked) return; // Progressive disclosure — locked chapters are inert.
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
}

export default function ChapterRail() {
  const location = useLocation();
  const { unlockedUpTo, active } = useChapterProgress();
  const onJump = useChapterJump();

  if (location.pathname !== "/") return null;

  return (
    <aside
      data-testid="chapter-rail"
      aria-label="Tour chapters"
      className="hidden lg:flex fixed right-7 top-1/2 -translate-y-1/2 z-40 flex-col items-end gap-5"
    >
      {CHAPTERS.map((c, i) => (
        <ChapterButton
          key={c.id}
          chapter={c}
          isActive={i === active}
          unlocked={i <= unlockedUpTo}
          onJump={onJump}
        />
      ))}
    </aside>
  );
}
